import { Box, Divider, Flex, Text } from '@chakra-ui/react';

import type { SelectionList } from '@revelio/iso-utility';

import { resolveSelectionListItemLabel } from '../../components/custom-filters/data-builder-selection-list-mapping';
import type {
  CustomFilter,
  CustomFilterCondition,
  SelectionListSelectedItem,
} from '../../data-builder.store';
import type { FieldGranularityMap, FieldLabelMap } from './DatasetSummaryCard';
import { SummaryPopover, SummaryTrigger } from './SummaryPrimitives';

function resolveItemLabel(
  item: SelectionListSelectedItem,
  selectionLists?: SelectionList[]
): string {
  if (!selectionLists?.length) return item.label;
  return (
    resolveSelectionListItemLabel(
      item.id,
      item.selectionListId,
      selectionLists
    ) ?? item.label
  );
}

const formatConditionValue = (
  condition: CustomFilterCondition,
  selectionLists?: SelectionList[],
  fieldGranularityMap?: FieldGranularityMap
): string => {
  if (
    Array.isArray(condition.selectionListItems) &&
    condition.selectionListItems.length > 0
  ) {
    return condition.selectionListItems
      .map((item) => {
        const label = resolveItemLabel(item, selectionLists);
        // Use the per-item selectionListId for granularity (handles merged
        // multi-key conditions where items come from different granularity levels)
        const granularity = fieldGranularityMap?.[item.selectionListId];
        return granularity ? `${label} (${granularity})` : label;
      })
      .join(', ');
  }
  const granularity = fieldGranularityMap?.[condition.field];
  const suffix = granularity ? ` (${granularity})` : '';
  return condition.value.map((v) => v + suffix).join(', ');
};

export const ConditionDetail = ({
  condition,
  binary,
  showBinary,
  selectionLists,
  fieldLabelMap,
  fieldGranularityMap,
}: {
  condition: CustomFilterCondition;
  binary: 'AND' | 'OR' | null;
  showBinary: boolean;
  selectionLists?: SelectionList[];
  fieldLabelMap?: FieldLabelMap;
  fieldGranularityMap?: FieldGranularityMap;
}) => {
  return (
    <>
      <Flex justifyContent="flex-start" gap="2">
        <Text as="span" textColor="blue.600">
          {fieldLabelMap?.[condition.field] ?? condition.field}
        </Text>
        <Text as="span" textColor="yellow.600">
          {condition.operator}
        </Text>
        <Text>
          {formatConditionValue(condition, selectionLists, fieldGranularityMap)}
        </Text>
      </Flex>
      {showBinary && binary !== null && (
        <Flex marginY="2" alignItems="center">
          <Divider orientation="horizontal" borderBottomWidth="2px" m="0" />
          <Text px="1">{binary}</Text>
          <Divider orientation="horizontal" borderBottomWidth="2px" m="0" />
        </Flex>
      )}
    </>
  );
};

export const FiltersSummary = ({
  filters,
  selectionLists,
  fieldLabelMap,
  fieldGranularityMap,
}: {
  filters: CustomFilter[];
  selectionLists?: SelectionList[];
  fieldLabelMap?: FieldLabelMap;
  fieldGranularityMap?: FieldGranularityMap;
}) => {
  if (filters.length === 0) {
    return <>None</>;
  }

  return (
    <>
      {filters.map((filter, index) => {
        const isLast = index === filters.length - 1;

        return (
          <SummaryPopover
            key={filter.id}
            trigger={
              <Box as="span" mr="1">
                <SummaryTrigger text={filter.name} />
                {!isLast && ','}
              </Box>
            }
          >
            {filter.conditions.map((condition, conditionIndex) => (
              <ConditionDetail
                key={`${filter.id}-${condition.field}-${conditionIndex}`}
                condition={condition}
                binary={filter.binary}
                showBinary={conditionIndex !== filter.conditions.length - 1}
                selectionLists={selectionLists}
                fieldLabelMap={fieldLabelMap}
                fieldGranularityMap={fieldGranularityMap}
              />
            ))}
          </SummaryPopover>
        );
      })}
    </>
  );
};
